<template>
  <div 
    :class="componentClasses"
  >
    <div class="d-flex flex-column align-items-start justify-content-between">
      <div class="w-100 d-flex align-items-center justify-content-between gap-1">
        <span class="text-primary-darker font-weight-bold font-size-100">
          {{ tagField.display_name }}
        </span>
        <b-badge
          v-if="tagField.is_featured"
          variant="secondary-100"
          class="text-muted"
          >Featured</b-badge
        >
      </div>
      <div class="w-100 d-flex align-items-baseline justify-content-between gap-1">
        <span class="text-muted small"> {{ tagField.description }} </span>
        <span
          v-if="canTag && !isSingleValue"
          class="text-muted small special-text text-nowrap"
          >{{ selectedTagIds.length }}/{{ availableTagsLength }} selected</span
        >
      </div>
    </div>
    <div v-if="stillLoading" key="isLoading">
      Loading...
    </div>
    <div
      v-else-if="canEdit || selectedTagIds.length !== 0"
      key="hasTags"
      class="w-100 d-flex align-items-center justify-content-between rounded"
    >
      <div v-if="canEdit && finalLayout === 'dropdown'" key="dropdownLayoutEdit" class="mt-1 w-100">
        <!-- we only use this in edit mode... otherwise we display tags in grid?  or some other display-only way? -->
        <PrimeDropdown
          v-if="isSingleValue"
          :key="`${tagField.id}-dropdown-single`"
          :id="`${tagField.id}-dropdown-single`"
          v-model="selectedTagIds[0]"
          :options="availableTags"
          optionLabel="name" 
          optionValue="id"
          dataKey="id" 
          :filter="availableTags && availableTags.length > 6" 
          placeholder="Select one..." 
          append-to="body"
          :disabled="stillLoading || !canTag"
          class="w-100 spl-tag-field-editor-dropdown"
          @change="handleDropdownChange"
          @input="handleDropdownInput"
        ></PrimeDropdown>
        <PrimeMultiSelect 
          v-else
          :key="`${tagField.id}-dropdown-multi`"
          :id="`${tagField.id}-dropdown-multi`"
          v-model="selectedTagIds" 
          :options="availableTags" 
          optionLabel="name" 
          optionValue="id"
          dataKey="id" 
          :filter="availableTags && availableTags.length > 6" 
          placeholder="Select..." 
          display="chip"
          append-to="body"
          :selectionLimit="isSingleValue ? 1 : null"
          :disabled="stillLoading || !canTag"
          class="w-100 spl-tag-field-editor-multiselect"
          @change="handleMultiselectChange"
          @input="handleMultiselectInput"
        >   <!-- @input="handleSelection" optionValue="id" - up/down arrows did not work when using optionValue -- it screws up what is shown in value slot I think -->
            <template #indicator>
                <span class="p-dropdown-trigger-icon pi pi-chevron-down"></span>  <!-- had to add this slot, even though it has default -->
            </template>
            <template #option="slotProps">
                {{ slotProps.option['name'] }}
            </template>
        </PrimeMultiSelect>
      </div>
      <div 
        v-else key="gridLayout"
        class="my-1"
        :style="tagWrapperStyles"
      >
        <div
          v-for="tag in canEdit ? availableTags : selectedTagObjects"
          :key="getTagUniqueId(tag)"
          class="mb-1"
          :style="tagOuterStyles"
        >
          <div
            :class="[canEdit ? 'editable' : 'not-editable', getIsTagSelected(tag) ? 'is-checked' : 'not-checked']"
            class="spl-tag-field-editor-option field-checkbox d-inline-flex align-items-center pl-2"
          >
            <template v-if="canEdit">
              <RadioButton
                v-if="isSingleValue"
                :id="getTagUniqueId(tag)"
                :value="tag.id"
                v-model="selectedTagIds[0]"
                @change="handleChange(tag)"
              />
              <Checkbox
                v-else
                :id="getTagUniqueId(tag)"
                :value="tag.id"
                v-model="selectedTagIds"
                @change="handleChange(tag)"
              />
            </template>
            <b-icon
              v-else
              :id="getTagUniqueId(tag)"
              :icon="getIsTagSelected(tag) ? 'check-circle-fill' : 'x-circle'"
              :variant="getIsTagSelected(tag) ? 'primary' : 'secondary-100'"
              style="height: 20px; width: 20px"
            ></b-icon>
            <label
              :for="getTagUniqueId(tag)"
              class="mb-0 px-2 d-flex align-items-center"
              style="padding-top: 6px; padding-bottom: 6px"
            >
              <TaggingBadge
                v-if="displayType === 'BADGE'"
                :label="tag.name"
                :variant="tag.color_variant"
              />
              <span v-else>{{ tag.name }}</span>
            </label>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="special-text text-muted mt-2">  <!-- NON-EDIT MODE and NO TAGS SELECTED -->
      None Selected
    </div>
    <!-- <div class="">
      <pre>{{ this.selectedTagIds }}</pre>
    </div> -->
  </div>
</template>

<script>
  import { suppliosApi } from '@/js/api-service';

  import TaggingBadge from '@/components/taxonomy/taggings/TaggingBadge.vue';

  import Checkbox from 'primevue/checkbox';
  import RadioButton from 'primevue/radiobutton';
  import PrimeMultiSelect from 'primevue/multiselect';
  import PrimeDropdown from 'primevue/dropdown';

  /* 
  CSS Grid styling for multiple cols:
  ceil(items/cols) = ROWS -- need to calculate this to know the number of rows, the use:
  .wrapper {
    width: 100%;
    display: grid;
    grid-auto-flow: column;
    grid-template-rows: repeat(ROWS, 1fr);
    gap: 0.5rem;
  }
  
  */

  export default {
    name: 'TagFieldEditor',
    components: { Checkbox, RadioButton, TaggingBadge, PrimeMultiSelect, PrimeDropdown },
    props: {
      tagField: {
        type: Object,
        required: true,
      },
      value: {
        type: Array,
        required: true,
        default: () => [],
      },
      isEditing: {
        type: Boolean,
        required: true,
        default: false,
      },
      objectId: {  // empty if we are creating a new object, otherwise the ID of the object we are tagging
        type: String,
        required: false,
      },
      layout: {
        type: String,
        default: 'vertical',  // options are 'horizontal' or 'vertical' or 'dropdown' (select for single, multiselect for multiple)
      },
      // TODO - pass some 'rules' object to control front-end rules (maybe hard to enforce on backend, but at least can do it on frontend) - min/max tags, etc.
    },
    data() {
      return {
        stillLoading: true,
        selectedTagIds: [], // this.value,
        availableTags: [],
      };
    },
    methods: {
      async getAvailableTags() {
        // OLD - can't just get all from a group!  const { data: tags } = await suppliosApi.tags.tag.getByGroup(this.tagField.group);
        const objectId = this.objectId || null;  // falsey value should be null, not empty string, just to be safe
        const { data: tags } = await suppliosApi.tags.tagField.get_available_tags(this.tagField.id, objectId);
        this.availableTags = tags;
        this.stillLoading = false;
      },
      getTagUniqueId(tagObj) {  // use this to set the HTML id on the input elements, but NOT for tracking the local value (selectedTagIds)
        return `${this.tagField.id}_${tagObj.id}`;
      },
      getIsTagSelected(tag) {
        //return this.selectedTagIds.some((selectedTag) => selectedTag.id === tag.id);
        return this.selectedTagIds.some(selectedIds => selectedIds === tag.id);
      },
      // handleInput(tagObj) {
      //   console.log('from handleInput - tabObj', tagObj);
      //   console.log('from handleInput - selectedTagIds', this.selectedTagIds);
      // },
      handleChange(tagObj) {
        // console.log('from handleChange - value is:', tagObj);
        // this gets a tag object, but the local value (selectedTagIds) is updated automatically from v-model on inputs
        let newTagList = [];  // this is what we actually EMIT, must be objects in/out of this component, but locally we track things by ID
        if (this.isSingleValue) {
          newTagList = this.getTagObjectsFromIds([tagObj.id]);
        } else {
          newTagList = this.getTagObjectsFromIds(this.selectedTagIds);
        }
        // Always emit an array, regardless of single or multiple value context
        // console.log('from handleChange - we will emit this:', newTagList);
        // this.$emit('input', newTagList);
        this.emitInput(newTagList);
      },
      emitInput(tabObjectList) {
        // standardized way of emitting 'input' event to parent component
        // we must emit an array of tag objects, not just IDs, but internally we can track by ID, and we have multiple types of components to manage things locally
        // this normalizes all of that so we always emit the same type of data
        // console.log(`from TagFieldEditor for tagField ${this.tagField.display_name} (id: ${this.tagField.id}) - we will emit this:`, tabObjectList);
        this.$emit('input', tabObjectList);
      },
      handleMultiselectChange(payload) {
        // This is called when the multiselect selection changes, payload is { value: [selectedIds], originalEvent: event }
        const selectedIds = payload.value;
        // console.log('from handleMultiselectChange - selectedIds:', selectedIds);
        let newTagList = this.getTagObjectsFromIds(selectedIds);
        this.emitInput(newTagList);
      },
      handleMultiselectInput(selectedIds) {
        // payload in multiselect is the selected IDs, not the objects
        // we probably don't need this because we handle the change event above, but keeping it for reference
        console.log('from handleMultiselectInput - event:', selectedIds);
      },
      handleDropdownChange(payload) {
        // This is called when the dropdown selection changes, payload is { value: selectedIds, originalEvent: event }
        const selectedId = payload.value;
        // console.log('from handleDropdownChange - selectedId:', selectedId);
        // this will only be for a single value... but we always work with arrays in this component
        const selectedIds = Array.isArray(selectedId) ? selectedId : [selectedId];
        let newTagList = this.getTagObjectsFromIds(selectedIds);
        this.emitInput(newTagList);
      },
      handleDropdownInput(selectedId) {
        // payload in multiselect is the selected IDs, not the objects
        // we probably don't need this because we handle the change event above, but keeping it for reference
        console.log('from handleDropdownInput - event:', selectedId);
      },
      getTagObjectsFromIds(tagIds) {
        return this.availableTags.filter(t => tagIds.includes(t.id));
      },
      setselectedTagIdsFromArrayOfObjects(incomingValue) {
        this.selectedTagIds = Array.isArray(incomingValue) ? incomingValue.map(t => t.id) : [];
      },
    },
    computed: {
      isSingleValue() {
        return this.tagField.value_type === 'SINGLE';
      },
      availableTagsLength() {
        return Array.isArray(this.availableTags) ? this.availableTags.length : 0;
      },
      selectedTagsLength() {
        return Array.isArray(this.selectedTagIds) ? this.selectedTagIds.length : 0;
      },
      canTag() {
        return this.tagField.can_edit_taggings;
      },
      canEdit() {
        return this.canTag && this.isEditing;
      },
      availableTagIds() {
        return Array.isArray(this.availableTags) ? this.availableTags.map(t => t.id) : [];
      },
      selectedTagObjects() {
        return this.getTagObjectsFromIds(this.selectedTagIds);
      },
      finalLayout() {
        if (this.layout === 'dropdown') {
          return 'dropdown';  // TODO - clean this up... and check that we can support it with other options (value_type, display_type, logic_type)
        }
        return ['horizontal', 'vertical'].includes(this.layout) ? this.layout : 'vertical';
      },
      displayType() {
        return this.tagField?.display_type || 'TEXT';  // will be 'TEXT' or 'BADGE'
      },
      valueType() {
        return this.tagField?.value_type || '';  // will be 'SINGLE' or 'MULTIPLE'
      },
      logicType() {
        return this.tagField?.logic_type || '';  // will be 'FLAT' or 'NESTED'
      },
      componentClasses() {
        return [
          'spl-tag-field-editor',
          `spl-tag-field-editor-${this.tagField.id}`,
          `spl-tag-field-editor-${this.isEditing ? 'editable' : 'not-editable'}`,
          `spl-tag-field-editor-logic-type${this.logicType.toLowerCase()}`,
          `spl-tag-field-editor-display-type-${this.displayType.toLowerCase()}`,
          `spl-tag-field-editor-value-type-${this.valueType.toLowerCase()}`,
          `spl-tag-field-editor-layout-${this.finalLayout.toLowerCase()}`,
        ];
      },
      // COMPUTED STYLES FOR MANAGING TAG LAYOUT
      visibleTagCount() {
        return this.canEdit ? this.availableTagsLength : this.selectedTagsLength;
      },
      colCount() {
        return 4;  // future: make this dynamic based on screen size
      },
      rowCount() {
        return Math.ceil(this.visibleTagCount / this.colCount);
      },
      tagOuterStyles() {
        // styles on EACH TAG (outer div)
        //return this.finalLayout === 'horizontal' ? 'width: 25%; padding: 0 0.5rem;' : '';
        return '';
      },
      tagWrapperStyles() {
        // styles for the wrapper around all tags
        return `width: 100%; display: grid; grid-auto-flow: ${this.finalLayout === 'horizontal' ? 'row' : 'column'}; grid-template-columns: repeat(${this.colCount}, 1fr); grid-template-rows: repeat(${this.rowCount}, 1fr); gap: 0.25rem;`;
      },
    },
    watch: {
      value: {
        handler(newValue) {
          // need this to watch for changes from parent component passed in via value prop
          // console.log('from watch value - newValue:', newValue);
          this.setselectedTagIdsFromArrayOfObjects(newValue);
        },
        immediate: true,
        deep: true,
      },
    },
    created() {
      this.getAvailableTags();
    },
  };
</script>

<style lang="scss">
  .spl-tag-field-editor {

    .spl-tag-field-editor-multiselect {
      &.p-multiselect.p-multiselect-chip {
        .p-multiselect-label {
          display: flex;
          flex-wrap: wrap;
          row-gap: 0.25rem;
          column-gap: 0.5rem;
          .p-multiselect-token {
            // these are the selected chips in the multiselect dropdown - we remove their standard margin since we have flex gap instead
            margin: 0;
          }
        }
      }
    }

    .spl-tag-field-editor-option {
      background-color: transparent;
      border-radius: 0.25rem;
      border-width: 1px;
      border-style: solid;
      border-color: transparent;
      transition: 0.15s all;
      &.editable {
        background-color: $gray-50;
        border-color: $gray-100;
      }
      &:hover.editable {
        background-color: $gray-150;
        border-color: $gray-200;
      }
      &.is-checked.editable {
        background-color: $gray-100;
        border-color: $gray-200;
        &:hover {
          background-color: $gray-200;
          border-color: $gray-300;
        }
      }
      cursor: pointer;
      label {
        cursor: pointer;
      }
      &.not-editable {
        cursor: default;
        label {
          cursor: default;
        }
        &.not-checked label {
          //color: var(--gray-400);
          opacity: 0.5;
        }
      }
    }
  }
</style>
